// One cache per mounted report and tenant. Completed data stays fresh for 30 seconds.
export default function createReportRequestCache(fetcher, { ttl = 30000, delay = 180 } = {}) {
  const entries = new Map()

  const request = (url, { signal } = {}) => {
    if (signal?.aborted) return Promise.reject(new DOMException('Aborted', 'AbortError'))
    let entry = entries.get(url)
    if (entry?.expires <= Date.now()) {
      entries.delete(url)
      entry = null
    }
    if (!entry) {
      const controller = new AbortController()
      entry = { controller, subscribers: 0, started: false, expires: Infinity }
      entries.set(url, entry)
      entry.promise = new Promise((resolve, reject) => {
        entry.reject = reject
        entry.timer = setTimeout(async () => {
          entry.started = true
          try {
            const response = await fetcher(url, { method: 'GET', signal: controller.signal })
            if (!response || response.ok === false) throw new Error('No se pudo cargar el reporte')
            const data = await response.json()
            if (data?.errors?.length) throw new Error(data.errors.join(', '))
            if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError')
            entry.expires = Date.now() + ttl
            // Bound retained data while leaving in-flight requests available for reuse.
            for (const [key, value] of entries) {
              if (entries.size <= 50) break
              if (value.expires !== Infinity) entries.delete(key)
            }
            resolve(data)
          } catch (error) {
            if (entries.get(url) === entry) entries.delete(url)
            reject(error)
          }
        }, delay)
      })
    }
    entry.subscribers += 1
    return new Promise((resolve, reject) => {
      let finished = false
      const finish = () => {
        if (finished) return false
        finished = true
        signal?.removeEventListener('abort', abort)
        entry.subscribers -= 1
        return true
      }
      const abort = () => {
        if (!finish()) return
        // Cancel transient tab selections before they reach the server.
        if (!entry.started && entry.subscribers === 0) {
          clearTimeout(entry.timer)
          entries.delete(url)
          entry.reject(new DOMException('Aborted', 'AbortError'))
        }
        reject(new DOMException('Aborted', 'AbortError'))
      }
      signal?.addEventListener('abort', abort, { once: true })
      entry.promise.then(
        (data) => { if (finish()) resolve({ json: async () => data }) },
        (error) => { if (finish()) reject(error) }
      )
    })
  }

  request.dispose = () => {
    for (const entry of entries.values()) {
      clearTimeout(entry.timer)
      entry.controller.abort()
      if (!entry.started) entry.reject(new DOMException('Aborted', 'AbortError'))
    }
    entries.clear()
  }
  return request
}
